// ===========================================
// The family's late-fee exemption (#372 plan A6.3)
// ===========================================
// Stops late FEES on every invoice of this family; reminders still go. Set with a staff-only
// reason, cleared without one. Changing it needs `operations_invoices.write` AND
// `operations_pricing.delete`; without them it is shown read-only.

import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { formatInSchoolTz } from '../../../utils/dateUtils';
import { useClearLateFeeExemption, useSetLateFeeExemption } from '../../../hooks/useFamilyPayments';
import LateFeeReasonDialog from '../../invoices/LateFeeReasonDialog';

const DAY: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

export interface LateFeeExemptionLineProps {
  familyId: string;
  exemption: { at: string; byName: string | null; reason: string } | null | undefined;
  timeZone: string;
  canChange: boolean;
}

export default function LateFeeExemptionLine({ familyId, exemption, timeZone, canChange }: LateFeeExemptionLineProps) {
  const [open, setOpen] = useState(false);
  const setMutation = useSetLateFeeExemption();
  const clearMutation = useClearLateFeeExemption();

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Late fees
          </Typography>
          {exemption ? (
            <Typography variant="body2" color="text.secondary">
              Exempt since {formatInSchoolTz(exemption.at, timeZone, DAY)}
              {exemption.byName ? ` (${exemption.byName})` : ''}: {exemption.reason}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Charged as the school&apos;s late-fee settings say
            </Typography>
          )}
        </Box>
        {canChange &&
          (exemption ? (
            <Button
              size="small"
              variant="outlined"
              disabled={clearMutation.isPending}
              onClick={() => clearMutation.mutate(familyId)}
            >
              Clear exemption
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setMutation.reset();
                setOpen(true);
              }}
            >
              Exempt from late fees
            </Button>
          ))}
      </Stack>
      <LateFeeReasonDialog
        open={open}
        title="Exempt this family from late fees"
        explanation={[
          "No late fee will be added to any of this family's invoices while they are exempt. Reminder emails still go.",
          'If you clear it later, a fee whose date passed in the last 3 days can still be charged; older ones never are.',
        ]}
        label="Why? (for example: staff family)"
        confirmLabel="Exempt"
        loading={setMutation.isPending}
        error={setMutation.error}
        onClose={() => setOpen(false)}
        onConfirm={(reason) => setMutation.mutate({ familyId, reason }, { onSuccess: () => setOpen(false) })}
      />
    </Box>
  );
}
